import { pollWindowVariables } from '@mooncake/mooncake-gateway-ecom/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import { IIbexPricingDetail, IPromotion } from '../types/subscription';
import { getProductMapping, PROMOTION_TYPES } from '../constants';
import { TIbexPricingDetails } from '../types/module/window';
import { Googlesheet } from '../types/generatedContent';

export interface IWindowState {
  ibexPricingDetails: TIbexPricingDetails | undefined;
  windowFetchError: boolean;
  fetching: boolean;
  freeTrialItem: TFreeTrialPromo | boolean | undefined;
  freeTrialDetected: boolean | undefined;
  freeTrialProductId: string | undefined;
}

const initialState: IWindowState = {
  ibexPricingDetails: undefined,
  windowFetchError: false,
  fetching: true,
  freeTrialItem: undefined,
  freeTrialDetected: undefined,
  freeTrialProductId: undefined,
};

const windowSlice = createSlice({
  name: 'window',
  initialState: initialState,
  reducers: {
    setWindowVariables(state, action: PayloadAction<TWindowSliceData>) {
      const { payload } = action;
      state.ibexPricingDetails = payload.ibexPricingDetails;
      state.freeTrialDetected = payload.freeTrialDetected;
      state.freeTrialItem = payload.freeTrialItem;
      state.fetching = false;
    },
    setFetchingError(state) {
      state.windowFetchError = true;
      state.fetching = false;
    },
    toggleDetection(state) {
      state.freeTrialDetected = !state.freeTrialDetected;
    },
  },
});

export const { setWindowVariables, setFetchingError, toggleDetection } = windowSlice.actions;

type TWindowSliceData = {
  ibexPricingDetails: TIbexPricingDetails;
  freeTrialDetected: boolean;
  freeTrialItem: TFreeTrialPromo | boolean;
};
/*Fetch necessary windows variables*/
export const fetchWindowVariables = (content: Googlesheet) => {
  return async function (dispatch: Dispatch) {
    const [ibexPricingDetails]: [TIbexPricingDetails] = await pollWindowVariables(['ibexPricingDetails']);
    let variables = {
      ibexPricingDetails: ibexPricingDetails,
      freeTrialDetected: isFreeTrialCheck(ibexPricingDetails),
      freeTrialItem: findFreeTrialPromotion(ibexPricingDetails, content),
    };
    dispatch(setWindowVariables(variables));
  };
};

const isFreeTrialCheck = (ibexPricingDetails: TIbexPricingDetails): boolean => {
  if (ibexPricingDetails.subscriptions.length) {
    return ibexPricingDetails.subscriptions.some((subscription) =>
      subscription.promotions?.some((promotion) => promotion.type === PROMOTION_TYPES.FREE_TRIAL)
    );
  }
  return false;
};

export type TFreeTrialPromo = {
  duration: number;
  durationUnit: string;
  translatedDurationUnit: string;
  productId: string;
  productSubscriptionDuration: string;
  formattedBasePrice: string;
};

const findFreeTrialPromotion = (
  ibexPricingDetails: TIbexPricingDetails,
  content: Googlesheet
): TFreeTrialPromo | boolean => {
  let freeTrialSubItem: IPromotion = {};
  let foundSubItem: IIbexPricingDetail = {} as IIbexPricingDetail;
  ibexPricingDetails.subscriptions.forEach((subItem) => {
    if (subItem?.promotions && subItem?.promotions?.length > 0) {
      const item = subItem.promotions?.find((value) => value.type === PROMOTION_TYPES.FREE_TRIAL);
      if (item !== undefined) {
        freeTrialSubItem = item;
        foundSubItem = subItem;
      }
    }
  });

  const productMap = getProductMapping(foundSubItem.id, content);

  return freeTrialSubItem?.promotionID === undefined
    ? false
    : ({
        duration: freeTrialSubItem.duration,
        durationUnit: freeTrialSubItem.durationUnit,
        productId: foundSubItem.id,
        productSubscriptionDuration: productMap?.durationUnit,
        formattedBasePrice: foundSubItem.basePriceFormatted,
        translatedDurationUnit: productMap?.translatedDurationUnit,
      } as TFreeTrialPromo);
};
export default windowSlice.reducer;
